import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
} from 'react';
import {
  OnboardingStepId,
  OnboardingSubStepId,
  OnboardingData,
  OnboardingContextValue,
} from '../types';

const OnboardingContext = createContext<OnboardingContextValue | null>(null);

const ADMIN_STEP_ORDER: OnboardingStepId[] = [
  'your-name',
  'your-role',
  'detail-level',
  'technical-level',
  'choose-what-to-do-first',
];

const NON_ADMIN_STEP_ORDER: OnboardingStepId[] = [
  'your-name',
  'your-role',
  'detail-level',
  'technical-level',
  'choose-what-to-do-first',
];

// Sub-steps for each main step
const ABOUT_YOU_SUB_STEPS: OnboardingSubStepId[] = [
  'name',
  'role',
  'detail-level',
  'technical-level',
];

const ABOUT_COMPANY_SUB_STEPS: OnboardingSubStepId[] = [
  'company-name',
  'company-website',
  'company-focus',
];

interface OnboardingProviderProps {
  children: React.ReactNode;
  isAdmin?: boolean;
  initialStep?: OnboardingStepId;
  hasUserMemory?: boolean;
  hasCompanyMemory?: boolean;
}

// Helper function to get sub-steps for a step
const getSubStepsForStep = (step: OnboardingStepId): OnboardingSubStepId[] => {
  switch (step) {
    case 'about-you':
      return ABOUT_YOU_SUB_STEPS;
    case 'about-company':
      return ABOUT_COMPANY_SUB_STEPS;
    default:
      return [];
  }
};

// Helper function to filter step order based on memory presence
const getFilteredStepOrder = (
  isAdmin: boolean,
  hasUserMemory: boolean,
): OnboardingStepId[] => {
  const baseOrder = isAdmin ? ADMIN_STEP_ORDER : NON_ADMIN_STEP_ORDER;

  // If user has memory, skip all the "about you" steps
  if (hasUserMemory) {
    return baseOrder.filter(
      (step) =>
        step !== 'your-name' &&
        step !== 'your-role' &&
        step !== 'detail-level' &&
        step !== 'technical-level',
    );
  }
  return baseOrder;
};

export const OnboardingProvider: React.FC<OnboardingProviderProps> = ({
  children,
  isAdmin = false,
  initialStep,
  hasUserMemory = false,
  hasCompanyMemory: _hasCompanyMemory = false,
}) => {
  // Filter step order based on memory presence
  const stepOrder = useMemo(
    () => getFilteredStepOrder(isAdmin, hasUserMemory),
    [isAdmin, hasUserMemory],
  );

  // Set initial step to first step in filtered order if not provided
  const computedInitialStep = initialStep || stepOrder[0] || 'your-name';

  const [currentStep, setCurrentStep] =
    useState<OnboardingStepId>(computedInitialStep);
  const [data, setData] = useState<OnboardingData>({});

  // Initialize sub-step based on current step (only for steps that have sub-steps)
  const [currentSubStep, setCurrentSubStep] = useState<
    OnboardingSubStepId | undefined
  >(() => {
    const subSteps = getSubStepsForStep(computedInitialStep);
    return subSteps.length > 0 ? subSteps[0] : undefined;
  });

  const updateData = useCallback((newData: Partial<OnboardingData>) => {
    setData((prev) => ({ ...prev, ...newData }));
  }, []);

  const goToStep = useCallback((step: OnboardingStepId) => {
    setCurrentStep(step);
    const subSteps = getSubStepsForStep(step);
    setCurrentSubStep(subSteps.length > 0 ? subSteps[0] : undefined);
  }, []);

  const goToSubStep = useCallback((subStep: OnboardingSubStepId) => {
    setCurrentSubStep(subStep);
  }, []);

  const goToNextStep = useCallback(() => {
    const subSteps = getSubStepsForStep(currentStep);
    if (subSteps.length > 0 && currentSubStep) {
      // Navigate to next sub-step
      const currentSubIndex = subSteps.indexOf(currentSubStep);
      if (currentSubIndex < subSteps.length - 1) {
        setCurrentSubStep(subSteps[currentSubIndex + 1]);
        return;
      }
    }

    // Move to next main step
    const currentIndex = stepOrder.indexOf(currentStep);
    if (currentIndex < stepOrder.length - 1) {
      const nextStep = stepOrder[currentIndex + 1];
      setCurrentStep(nextStep);
      const nextSubSteps = getSubStepsForStep(nextStep);
      setCurrentSubStep(nextSubSteps.length > 0 ? nextSubSteps[0] : undefined);
    }
  }, [currentStep, currentSubStep, stepOrder]);

  const goToPreviousStep = useCallback(() => {
    const subSteps = getSubStepsForStep(currentStep);
    if (subSteps.length > 0 && currentSubStep) {
      // Navigate to previous sub-step
      const currentSubIndex = subSteps.indexOf(currentSubStep);
      if (currentSubIndex > 0) {
        setCurrentSubStep(subSteps[currentSubIndex - 1]);
        return;
      }
    }

    // Move to previous main step
    const currentIndex = stepOrder.indexOf(currentStep);
    if (currentIndex > 0) {
      const prevStep = stepOrder[currentIndex - 1];
      setCurrentStep(prevStep);
      const prevSubSteps = getSubStepsForStep(prevStep);
      if (prevSubSteps.length > 0) {
        setCurrentSubStep(prevSubSteps[prevSubSteps.length - 1]);
      } else {
        setCurrentSubStep(undefined);
      }
    }
  }, [currentStep, currentSubStep, stepOrder]);

  const canGoBack = useMemo(() => {
    const subSteps = getSubStepsForStep(currentStep);
    if (subSteps.length > 0 && currentSubStep) {
      const currentSubIndex = subSteps.indexOf(currentSubStep);
      if (currentSubIndex > 0) {
        return true;
      }
    }
    return stepOrder.indexOf(currentStep) > 0;
  }, [currentStep, currentSubStep, stepOrder]);

  const canGoNext = useMemo(() => {
    // Validate current sub-step data before allowing next
    if (currentSubStep) {
      switch (currentSubStep) {
        case 'name':
          return !!data.name && data.name.trim().length > 0;
        case 'role':
          return (
            !!data.role && (data.role !== 'something-else' || !!data.roleOther)
          );
        default:
          return true;
      }
    }

    // Validate main step
    switch (currentStep) {
      case 'your-name':
        return !!data.name && data.name.trim().length > 0;
      case 'your-role':
        return (
          !!data.role && (data.role !== 'something-else' || !!data.roleOther)
        );
      default:
        return true;
    }
  }, [currentStep, currentSubStep, data]);

  const isComplete = useMemo(() => {
    return currentStep === 'choose-what-to-do-first';
  }, [currentStep]);

  const value = useMemo<OnboardingContextValue>(
    () => ({
      currentStep,
      currentSubStep,
      data,
      isAdmin,
      stepOrder,
      goToNextStep,
      goToPreviousStep,
      goToStep,
      goToSubStep,
      updateData,
      canGoBack,
      canGoNext,
      isComplete,
    }),
    [
      currentStep,
      currentSubStep,
      data,
      isAdmin,
      stepOrder,
      goToNextStep,
      goToPreviousStep,
      goToStep,
      goToSubStep,
      updateData,
      canGoBack,
      canGoNext,
      isComplete,
    ],
  );

  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  );
};

export const useOnboarding = () => {
  const context = useContext(OnboardingContext);
  if (!context) {
    throw new Error('useOnboarding must be used within OnboardingProvider');
  }
  return context;
};
